import React from "react";
import { Editor as EditorClass } from "tinymce";
import { ClassNameProps, makeClassName, StackingContext, StyleProps } from "@hex-insights/core";
import { settings, TextFieldInputProps } from "@hex-insights/forms";
import { Editor } from "@tinymce/tinymce-react";
import "./styles.css";
import styles from "./styles.module.css";

const apiKey = process.env.REACT_APP_TINY_MCE_API_KEY;

export type RichTextInputProps = TextFieldInputProps;

export function RichTextInput({
	id,
	name,
	value,
	setValue,
	onFocus,
	onBlur: onBlurProp,
	placeholder,
	autoFocus,
	disabled,
}: RichTextInputProps) {
	const editorRef = React.useRef<EditorClass | null>(null);

	const onBlur = React.useCallback(() => {
		if (!editorRef.current || !setValue) {
			return;
		}
		const content = editorRef.current.getContent();
		setValue(content);
		if (onBlurProp) {
			onBlurProp();
		}
	}, [setValue, onBlurProp]);

	const { stackIndex } = React.useContext(StackingContext);
	React.useEffect(() => {
		const zIndexName = "--tiny-mce__toolbar---z-index";
		document.body.style.setProperty(zIndexName, (stackIndex + 100).toString());
		return () => {
			document.body.style.removeProperty(zIndexName);
		};
	}, [stackIndex]);

	return (
		<Editor
			id={id ?? name}
			initialValue={value}
			onFocus={onFocus}
			onBlur={onBlur}
			apiKey={apiKey}
			disabled={disabled}
			onInit={(_evt, editor) => (editorRef.current = editor)}
			init={{
				placeholder,
				auto_focus: autoFocus || undefined,
				plugins: [
					"accordion",
					"advlist",
					"anchor",
					"autolink",
					"charmap",
					"code",
					"codesample",
					"directionality",
					"emoticons",
					"fullscreen",
					"help",
					"image",
					"importcss",
					"insertdatetime",
					"link",
					"lists",
					"media",
					"nonbreaking",
					"powerpaste",
					"preview",
					"quickbars",
					"searchreplace",
					"table",
					"template",
					"visualblocks",
					"visualchars",
					"wordcount",
				],
				menubar: "edit view insert format tools table help",
				toolbar:
					"undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image media table | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code | print | anchor codesample | ltr rtl | accordion accordionremove",
				toolbar_mode: "wrap",
				font_family_formats:
					"System Font; Helvetica=helvetica; Impact=impact,chicago; Loved by The King=Loved by the King,cursive; Montserrat=Montserrat,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times;",
				content_style:
					"@import url('https://fonts.googleapis.com/css2?family=Loved+by+the+King&family=Montserrat:ital,wght@0,100;0,400;0,700;1,100;1,400;1,700&display=swap');",
				quickbars_insert_toolbar: false,
				branding: false,
				images_upload_handler: async (blobInfo) => {
					const file = blobInfo.blob();
					const { ok, key, contentType } = await settings.uploads.uploadFile(file as File); // TODO hex-core update fix typecast
					if (!ok) {
						throw new Error("Failed to upload image. Please try again or use a different image.");
					}
					return settings.uploads.fileInfoToURL(key, contentType);
				},
				height: 500,
			}}
		/>
	);
}

export type RichTextDisplayProps = {
	value: string;
} & Partial<ClassNameProps & StyleProps>;

export function RichTextDisplay({ value, className, style }: RichTextDisplayProps) {
	return (
		<React.Fragment>
			<style>
				@import
				url('https://fonts.googleapis.com/css2?family=Loved+by+the+King&family=Montserrat:ital,wght@0,100;0,400;0,700;1,100;1,400;1,700&display=swap');
			</style>

			<div
				className={makeClassName(styles["rich-text-display"], className)}
				style={style}
				dangerouslySetInnerHTML={{ __html: value }}
			></div>
		</React.Fragment>
	);
}

export function stripHTMLFromString(s: string) {
	return s.replace(/(<([^>]+)>)|(&#?[A-Za-z0-9]+;)/g, "");
}
